<svelte:options runes={true} />

<script lang="ts">
  import IconChevronDown from "~icons/dusk/chevron-down";
  import { Accordion } from "bits-ui";
  import { slide } from "svelte/transition";
  import type { Filter } from "~/lib/filters/types";
  import Badge from "~/components/ui/Badge/Badge.svelte";
  import FilterControls from "~/components/filters/FilterControls/FilterControls.svelte";
  import { cubicOut } from "svelte/easing";

  interface Props {
    filter: Filter;
    value?: string[] | [number, number];
    activeCount?: number;
    onChange?: (id: string, value: string[] | [number, number]) => void;
    class?: string;
  }

  let { filter, value, activeCount, onChange, class: className = "" }: Props = $props();

  const SLIDE_DURATION = 300; // matches --da-duration-slow (0.3s)
</script>

{#snippet chevron()}
  <IconChevronDown
    class="da-filter-accordion-item__chevron"
    aria-hidden="true"
    width="20"
    height="20"
  />
{/snippet}

<Accordion.Item value={filter.id} class="da-filter-accordion-item {className}">
  <Accordion.Header class="da-filter-accordion-item__header">
    <Accordion.Trigger class="da-filter-accordion-item__trigger">
      <span class="da-filter-accordion-item__label">{filter.label}</span>
      {#if (activeCount ?? 0) > 0}
        <Badge
          as="span"
          text={String(activeCount)}
          color="charcoal"
          size="sm"
          class="da-filter-accordion-item__count"
        />
      {/if}
      {@render chevron()}
    </Accordion.Trigger>
  </Accordion.Header>
  <Accordion.Content forceMount={true} class="da-filter-accordion-item__content">
    {#snippet child({ props, open })}
      {#if open}
        <!-- eslint-disable-next-line svelte/no-unused-class-name -- test query anchor, no CSS by design -->
        <div
          class="da-filter-accordion-item__body"
          transition:slide={{ duration: SLIDE_DURATION, easing: cubicOut }}
          {...props}
        >
          <FilterControls {filter} {value} onChange={(v) => onChange?.(filter.id, v)} />
        </div>
      {/if}
    {/snippet}
  </Accordion.Content>
</Accordion.Item>

<style>
  @layer aurora {
    /* bits-ui renders these elements — use :global() */
    :global(.da-filter-accordion-item) {
      border-bottom: 1px solid var(--da-color-charcoal-500);
    }

    :global(.da-filter-accordion-item__header) {
      margin: 0;
    }

    :global(.da-filter-accordion-item__trigger) {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding-block: var(--da-space-4);
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-md);
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-charcoal-500);
      background: none;
      border: none;
      cursor: pointer;
    }

    :global(.da-filter-accordion-item__trigger:focus-visible) {
      outline: 2px solid var(--da-color-charcoal-500);
      outline-offset: 2px;
    }

    :global(.da-filter-accordion-item__label) {
      flex: 1;
      text-align: left;
    }

    :global(.da-filter-accordion-item__chevron) {
      flex-shrink: 0;
      transition: transform var(--da-duration-normal) ease;
    }

    :global(
      .da-filter-accordion-item__trigger[data-state="open"] .da-filter-accordion-item__chevron
    ) {
      transform: rotate(180deg);
    }

    :global(.da-filter-accordion-item__content) {
      overflow: hidden;
      padding-bottom: var(--da-space-4);
    }

    :global(.da-filter-accordion-item__content:has(:focus-visible)) {
      overflow: visible;
    }
  }
</style>
